import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { Formik } from 'formik'
import { makeStyles, shorthands, tokens } from '@fluentui/react-components'
import Loading from '../../../components/Loading'
import { Alert } from '@fluentui/react-components/unstable'
import useAxios from '../../../hooks/useAxios'
import { getId } from '@fluentui/react'
import FormContainer from './FormContainer'
import { AxiosResponse } from 'axios'
import * as Yup from 'yup'
import { DocketProceedingProps } from '../../../pages/File/Registration/DocketProceedingOverview'

export enum DocketProceedingType {
    SubdistrictCourt = 'SUBDISTRICT_COURT',
    DistrictCourt = 'DISTRICT_COURT',
    CourtOfAppeal = 'COURT_OF_APPEAL',
    SupremeCourt = 'SUPREME_COURT',
}

export type DocketProceedingDto = {
    id: string
    fileId: string
    userId: string
    type: DocketProceedingType
    judicialInstitutionId: string
    location?: string
    proceedingNumber: string
    status?: string
    remark?: string
    entries: DocketEntryDto[]
}

export type DocketEntryDto = {
    id: string
    date: string
    title: string
    status?: string
    supplement?: string
    remark?: string
    result?: string
}

const useFormStyles = makeStyles({
    alert: {
        ...shorthands.margin(0, tokens.spacingHorizontalS),
    },
})

const requiresType =
    (message?: string) =>
    ([type], schema) => {
        return type && type === DocketProceedingType.CourtOfAppeal ? schema.required(message) : schema.nullable()
    }

const ValidationSchema = Yup.object().shape({
    type: Yup.string().required(),
    fileId: Yup.string().uuid().required(),
    userId: Yup.string().uuid().required('Uitvoerder is een verplicht veld'),
    judicialInstitutionId: Yup.string().uuid().required('Justitiële instelling is een verplicht veld'),
    proceedingNumber: Yup.string().required('Zaakkenmerk is een verplicht veld'),
    status: Yup.string().nullable().when('type', requiresType('Status is een verplicht veld')),
})

export function DocketProceedingForm(props: DocketProceedingProps): ReactElement {
    const axios = useAxios()
    const styles = useFormStyles()
    const key = useMemo(() => getId(props.selected?.id), [props.docketProceedings.data, props.selected?.id])
    const initialValues = useMemo<DocketProceedingDto>(
        () =>
            props.file.loaded && props.parties.loaded && props.selected
                ? {
                      fileId: props.file.data.id,
                      userId: props.file.data.owner?.id,
                      ...props.docketProceedings.data?.find(proceeding => proceeding.id === props.selected.id),
                  }
                : undefined,
        [props.file.loaded, props.docketProceedings.data, props.selected?.id],
    )

    if (!props.file.loaded) {
        return <Loading />
    }

    if (!initialValues) {
        return (
            <div className={styles.alert}>
                <Alert intent="info">
                    Er is geen procedure geselecteerd. Kies een bestaande procedure uit de lijst of maak een nieuwe aan.
                </Alert>
            </div>
        )
    }

    return (
        <Formik<DocketProceedingDto>
            // Force reload the Formik component when we have selected another certificate, because otherwise the
            // pinned fields won't update correctly.
            key={key}
            validationSchema={ValidationSchema}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            initialValues={initialValues}
            onSubmit={async (dto: DocketProceedingDto, { setSubmitting }) => {
                const response: AxiosResponse<string> = props.selected.isNew
                    ? await axios.post('/registration/docket-proceeding', dto)
                    : await axios.put(`/registration/docket-proceeding/${props.selected.id}`, dto)

                // Set the stored docket proceeding as the current one.
                props.setSelected({
                    ...props.selected,
                    id: response.data,
                    type: dto.type,
                    isNew: false,
                    isDirty: false,
                })
                props.reset()

                setSubmitting(false)
            }}
        >
            <FormContainer {...props} />
        </Formik>
    )
}
